/* eslint-disable indent */
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { FormData, FormErrors, IUpsertAgentDialog } from './interfaces';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CustomTheme,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router';
import { isEmpty, isValidEmailAddress } from '../../../../helpers/formHelpers';
import useTrackFormChanges from '../../../../hooks/useTrackFormChanges';
import useFormState from '../../../../hooks/useFormState';
import {
  useCreateAgentMutation,
  useUpdateAgentMutation,
} from '../../../../reactQuery/Agents/agents';
import { ICreateAgentParams } from '../../../../api/agentsApi/interfaces';
import { MutateOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

export const UpsertAgentDialog: React.FC<IUpsertAgentDialog> = ({
  show,
  onClose,
  agent,
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const theme = useTheme<CustomTheme>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fullScreen = useMediaQuery(theme.breakpoints.down('tablet'));
  const isEditMode = !!agent;

  const { formData, setFormFieldData, setFormData } = useFormState<FormData>(
    {}
  );
  const { formErrors, setFormErrors } = useTrackFormChanges(formData);
  const [avatarImageSrc, setAvatarImageSrc] = useState<string | null>(null);

  const { mutate: createAgent, isLoading: isCreatingAgent } =
    useCreateAgentMutation();
  const { mutate: updateAgent, isLoading: isUpdatingAgent } =
    useUpdateAgentMutation();

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!formData.firstName || isEmpty(formData.firstName)) {
      errors.firstName = t('errors.required');
    }

    if (!formData.lastName || isEmpty(formData.lastName)) {
      errors.lastName = t('errors.required');
    }

    if (!formData.email || isEmpty(formData.email)) {
      errors.email = t('errors.required');
    } else if (!isValidEmailAddress(formData.email)) {
      errors.email = t('errors.invalidEmail');
    }

    if (!isEditMode && (!formData.password || isEmpty(formData.password))) {
      errors.password = t('errors.required');
    }

    setFormErrors(errors);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!Object.keys(formErrors).length) {
      const agentData: ICreateAgentParams = {
        firstName: formData.firstName as string,
        lastName: formData.lastName as string,
        email: formData.email as string,
      };

      if (formData.password) {
        agentData.password = formData.password;
      }

      if (formData.avatar !== undefined) {
        agentData.avatar = formData.avatar;
      }

      const mutateOptions:
        | MutateOptions<number, AxiosError, unknown>
        | undefined = {
        onSuccess: (agentId) => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updateAgent.success')
              : t('forms:createAgent.success'),
            {
              variant: 'success',
            }
          );

          navigate(`/agents/${agentId}`);
          onClose();
        },
        onError: () => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updateAgent.failed')
              : t('forms:createAgent.failed'),
            {
              variant: 'error',
            }
          );
        },
      };

      if (isEditMode) {
        updateAgent(
          {
            agentId: agent.id,
            agentData,
          },
          mutateOptions
        );
      } else {
        createAgent(agentData, mutateOptions);
      }
    }
  };

  const readFile = (file: File) => {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();

      reader.addEventListener(
        'load',
        () => resolve(reader.result as string),
        false
      );
      reader.readAsDataURL(file);
    });
  };

  const onUploadAvatar = (): void => {
    fileInputRef.current?.click();
  };

  const onAvatarFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const imageDataUrl = await readFile(file);

      setAvatarImageSrc(imageDataUrl);
      setFormFieldData('avatar', file);
    }
  };

  useEffect(() => {
    setFormErrors({});

    if (agent) {
      setFormData({
        firstName: agent.firstName,
        lastName: agent.lastName,
        email: agent.email,
      });

      setAvatarImageSrc(
        `${process.env.REACT_APP_API_URL}/agents/avatar/${agent.avatar}`
      );
    } else {
      setFormData({});
    }
  }, [show]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={show}
      keepMounted={false}
      //   TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            {isEditMode
              ? t('forms:updateAgent.title')
              : t('forms:createAgent.title')}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            disabled={isCreatingAgent || isUpdatingAgent}
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        dividers={true}
        sx={{
          minWidth: fullScreen ? undefined : '400px',
        }}
      >
        <Stack
          gap="15px"
          component="form"
          id="createAgentForm"
          onSubmit={handleSubmit}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Avatar
              alt="Sven van Zoelen"
              src={avatarImageSrc || ''}
              sx={{
                width: '120px',
                height: '120px',
                border: (theme) => `2px dashed ${theme.palette.primary.main}`,
                bgcolor: (theme) => theme.palette.grey[200],
                textAlign: 'center',
              }}
              onClick={onUploadAvatar}
            >
              <Typography color="text.primary">Click to upload</Typography>
            </Avatar>
            <input
              type="file"
              onChange={onAvatarFileChange}
              accept="image/*"
              hidden
              ref={fileInputRef}
            />
          </Box>

          <TextField
            label={t('entities:common.firstName')}
            variant="outlined"
            size="small"
            value={formData.firstName}
            placeholder={agent?.firstName}
            onChange={(e) => setFormFieldData('firstName', e.target.value)}
            error={!!formErrors.firstName}
            helperText={formErrors.firstName}
            required
          />

          <TextField
            label={t('entities:common.lastName')}
            variant="outlined"
            size="small"
            value={formData.lastName}
            placeholder={agent?.lastName}
            onChange={(e) => setFormFieldData('lastName', e.target.value)}
            error={!!formErrors.lastName}
            helperText={formErrors.lastName}
            required
          />

          <TextField
            label={t('entities:common.email')}
            variant="outlined"
            size="small"
            value={formData.email}
            placeholder={agent?.email}
            onChange={(e) => setFormFieldData('email', e.target.value)}
            error={!!formErrors.email}
            helperText={formErrors.email}
            type="email"
            required
          />

          <TextField
            label={t('entities:user.password')}
            variant="outlined"
            size="small"
            value={formData.password}
            onChange={(e) => setFormFieldData('password', e.target.value)}
            error={!!formErrors.password}
            helperText={formErrors.password}
            type="password"
            required={!isEditMode}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCreatingAgent || isUpdatingAgent}>
          {t('buttons.cancel')}
        </Button>
        <Button
          form="createAgentForm"
          type="submit"
          variant="contained"
          onClick={validateForm}
          disabled={isCreatingAgent || isUpdatingAgent}
        >
          {isEditMode ? t('buttons.update') : t('buttons.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
